/**
 * Wave 29 Chunk 8 — /team/attendance/:eid/override full-page form.
 *
 * Replaces the banned `OverrideModal` from AttendanceRegister.jsx
 * (DESIGN_CONTRACT Rule 0 — no small modal views).
 *
 * HR-only. Loads the original event, lets HR change kind / time / note,
 * with a MANDATORY audit reason. Delete option also lives here.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ChevronLeft, Loader2, Save, Trash2, AlertTriangle, ShieldCheck,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "../components/ui/sonner";
import VoiceTextarea from "../components/VoiceTextarea";
import { fmtDateTimeWithTz, toSastLocalInput, fromSastLocalInput } from "../lib/datetime";

const KIND_LABELS = {
  in: "Clock in",
  out: "Clock out",
  break_start: "Break start",
  break_end: "Break end",
};

export default function AttendanceOverride() {
  const { eid } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const isHr = user && ["owner", "admin"].includes(user.role);

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("in");
  const [at, setAt] = useState("");
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!eid) return;
    (async () => {
      setLoading(true);
      try {
        // The register endpoint returns events; we look up via the same path
        // so HR sees the row with its server-resolved fields.
        const { data } = await api.get(`/team/attendance/events/${eid}`).catch(async () => {
          const r = await api.get("/team/attendance/register", { params: { limit: 1000 } });
          const found = (r.data?.items || []).find((row) => row.id === eid);
          return { data: found };
        });
        if (!data) throw new Error("Event not found");
        setEvent(data);
        setKind(data.kind);
        // Bug 2 fix (Dirk 2026-05-13): datetime-local input must show SAST
        // wall-clock so the user can edit the time they actually see.
        setAt(toSastLocalInput(data.at));
        setNote(data.note || "");
      } catch (e) {
        toast.error(e?.response?.data?.detail || e?.message || "Could not load event.");
        nav("/team/attendance", { replace: true });
      } finally { setLoading(false); }
    })();
  }, [eid, nav]);

  if (!user) return null;
  if (!isHr) {
    return (
      <div className="hv-card p-6 text-center text-sm text-[#a0aabf]" data-testid="override-forbidden">
        <AlertTriangle className="w-6 h-6 mx-auto mb-2 text-amber-300" />
        Only owners and admins can override attendance events.
      </div>
    );
  }
  if (loading) return <div className="h-32 hv-card animate-pulse" data-testid="override-loading" />;
  if (!event) return null;

  const save = async (e) => {
    e?.preventDefault?.();
    if (reason.trim().length < 3) return toast.error("Reason required (min 3 chars).");
    setBusy(true);
    try {
      await api.patch(`/team/attendance/events/${event.id}`, {
        kind,
        // Convert SAST wall-clock input → UTC ISO for the backend.
        at: fromSastLocalInput(at),
        note: note.trim() || null,
        override_reason: reason.trim(),
      });
      toast.success("Override saved.");
      nav("/team/attendance");
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (reason.trim().length < 3) return toast.error("Reason required (min 3 chars).");
    if (!window.confirm("Permanently delete this event? An audit row will be kept.")) return;
    setBusy(true);
    try {
      await api.delete(`/team/attendance/events/${event.id}`, {
        params: { reason: reason.trim() },
      });
      toast.success("Deleted.");
      nav("/team/attendance");
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Delete failed.");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} className="space-y-4 max-w-2xl" data-testid="override-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link to="/team/attendance" className="hv-btn hv-btn-ghost text-xs" data-testid="override-back">
          <ChevronLeft className="w-3.5 h-3.5" /> Back to Register
        </Link>
        <h1 className="font-display text-xl sm:text-2xl font-bold flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-amber-300" /> Override event
        </h1>
        <span />
      </div>

      <div className="hv-card p-3 text-xs text-[#a0aabf] space-y-1" data-testid="override-original">
        <div>
          <span className="text-[#647087]">Employee:</span>{" "}
          <b className="text-white">{event.user_name || event.user_id?.slice(0, 8)}</b>
        </div>
        <div>
          <span className="text-[#647087]">Original:</span>{" "}
          {fmtDateTimeWithTz(event.at)} · {KIND_LABELS[event.kind] || event.kind}
        </div>
        {event.note && (
          <div>
            <span className="text-[#647087]">Original note:</span> {event.note}
          </div>
        )}
        {event.override_reason && (
          <div className="text-amber-300">
            Previously overridden by {event.overridden_by_name} — {event.override_reason}
          </div>
        )}
      </div>

      <div className="hv-card p-4 sm:p-5 space-y-4">
        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1">Kind</div>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            className="hv-input text-sm w-full"
            data-testid="override-kind"
          >
            {Object.entries(KIND_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1">
            Time <span className="text-[#1FB6C1] normal-case">(SAST · dd/mm/yyyy hh:mm)</span>
          </div>
          <input
            type="datetime-local"
            value={at}
            onChange={(e) => setAt(e.target.value)}
            className="hv-input text-sm w-full"
            data-testid="override-at"
          />
          <p className="text-[10px] text-[#647087] mt-1">
            Time is in SAST (Africa/Johannesburg). 24-hour clock.
          </p>
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1">Note</div>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="hv-input text-sm w-full"
            data-testid="override-note"
            placeholder="optional"
          />
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1">
            Reason (audit log) <span className="text-red-400">*</span>
          </div>
          <VoiceTextarea
            value={reason}
            onChange={setReason}
            rows={3}
            placeholder="e.g. Tech forgot to clock out before going home"
            data-testid="override-reason"
            required
           testIdPrefix="override-reason-voice" context="note" />
          <p className="text-[10px] text-[#647087] mt-1">
            Min 3 characters. Reason is logged with your name and the original values.
          </p>
        </label>
      </div>

      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={remove}
          disabled={busy || reason.trim().length < 3}
          className="hv-btn hv-btn-ghost text-xs text-red-300 disabled:opacity-50"
          data-testid="override-delete"
        >
          <Trash2 className="w-3 h-3" /> Delete event
        </button>
        <div className="flex items-center gap-2">
          <Link to="/team/attendance" className="hv-btn hv-btn-ghost text-xs">Cancel</Link>
          <button
            type="submit"
            disabled={busy || reason.trim().length < 3}
            className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
            data-testid="override-save"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            Save override
          </button>
        </div>
      </div>
    </form>
  );
}
